import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Dashboard,
  Schedule,
  Map,
  People,
  Autorenew,
  PersonAdd,
  Settings,
  SwapHoriz,
  AddCircle,
  CheckCircle,
  UploadFile,
  AccountBalance,
  Groups,
  Payments,
  Person,
  RateReview,
  History,
  Build,
  Star,
  Cancel,
  PhoneAndroid,
  Restaurant,
  PlayArrow,
  Stop,
  CameraAlt,
  WarningAmber,
  Description,
  Send,
  Download,
  ToggleOn,
  TrendingUp,
  QueryStats,
  SmartToyOutlined,
  VpnKey,
  Link as LinkIcon,
  ChatOutlined,
  FactCheck,
  ManageHistory,
  HelpOutline,
  Checklist,
  FeedbackOutlined as FeedbackIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';

interface FeatureGuideProps {
  title: string;
  icon?: React.ReactNode;
  description: string;
  steps: string[];
  stepsLabel?: string;
}

const FeatureGuide: React.FC<FeatureGuideProps> = ({ title, icon, description, steps, stepsLabel = 'How to use:' }) => (
  <Accordion sx={{ mb: 1 }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Box>
    </AccordionSummary>
    <AccordionDetails>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {description}
      </Typography>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {stepsLabel}
      </Typography>
      <List dense disablePadding>
        {steps.map((step, i) => (
          <ListItem key={i} disablePadding sx={{ alignItems: 'flex-start', mb: 0.5 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
              <Chip label={i + 1} size="small" sx={{ minWidth: 24, height: 24 }} />
            </ListItemIcon>
            <ListItemText primary={step} primaryTypographyProps={{ variant: 'body2' }} />
          </ListItem>
        ))}
      </List>
    </AccordionDetails>
  </Accordion>
);

/** Same order as Command Center (excluding Tutorials — this page is self-explanatory). */
const TOPICS = [
  { label: 'My Dashboard', icon: <Dashboard fontSize="small" /> },
  { label: 'Profile', icon: <Person fontSize="small" /> },
  { label: 'Settings', icon: <Settings fontSize="small" /> },
  { label: 'My Earnings', icon: <Payments fontSize="small" /> },
  { label: 'Financial Dashboard', icon: <AccountBalance fontSize="small" /> },
  { label: 'Additional Services', icon: <Build fontSize="small" /> },
  { label: 'Customer Agreements', icon: <Description fontSize="small" /> },
  { label: 'Crew Management', icon: <Groups fontSize="small" /> },
  { label: 'Crew App', icon: <PhoneAndroid fontSize="small" /> },
  { label: 'Reviews & Bids', icon: <RateReview fontSize="small" /> },
  { label: 'Service History / Cancellations', icon: <History fontSize="small" /> },
  { label: 'Renewals', icon: <Autorenew fontSize="small" /> },
  { label: 'AI Assistant (Pilot)', icon: <SmartToyOutlined fontSize="small" /> },
] as const;

type TopicLabel = (typeof TOPICS)[number]['label'];

/** Deep links like /provider/tutorials?topic=ai-assistant */
const TOPIC_SLUGS: Partial<Record<string, TopicLabel>> = {
  'ai-assistant': 'AI Assistant (Pilot)',
};

const ProviderTutorialsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TopicLabel>(TOPIC_SLUGS[searchParams.get('topic') || ''] || 'My Dashboard');
  const navigate = useNavigate();

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Provider Tutorials
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Guides for each Command Center page. Pick a topic below — no scrolling required.
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 3,
        }}
      >
        {TOPICS.map((topic) => (
          <Chip
            key={topic.label}
            icon={topic.icon}
            label={topic.label}
            onClick={() => setTab(topic.label)}
            color={tab === topic.label ? 'primary' : 'default'}
            variant={tab === topic.label ? 'filled' : 'outlined'}
            sx={{ fontWeight: tab === topic.label ? 600 : 400 }}
          />
        ))}
      </Box>

      <Paper sx={{ p: 3 }}>
        {/* My Dashboard (+ Scheduling, Routes, Customers, Today's Jobs, Onboarding) */}
        {tab === 'My Dashboard' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              My Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your home base in Command Center. You&apos;ll see Available Service Requests, This Week&apos;s Jobs,
              Today&apos;s Jobs, My Bids, and Payments. Use the dashboard tabs for Customer Management, Scheduling,
              and Route Optimization.
            </Typography>
            <FeatureGuide
              title="Available Service Requests"
              icon={<Schedule />}
              description="Marketplace leads in your area. You can run CRM, scheduling, routes, and Additional Services without bidding — marketplace requests need insurance (and pesticide for weed/fert) plus admin verification."
              steps={[
                'Open My Dashboard from Command Center.',
                'Browse Available Service Requests (empty until you are verified for marketplace bidding).',
                'Click Submit Bid to place a bid, or the eye icon for full details.',
                'Use Refresh to get the latest requests. Business tools still work while you wait on verification.',
              ]}
            />
            <FeatureGuide
              title="This Week's Jobs & Schedule Edit"
              icon={<Schedule />}
              description="Scheduled jobs for the week by day. Click the pencil icon next to a job to change the date (one-time or permanent)."
              steps={[
                'Find the job in This Week\'s Schedule.',
                'Click the pencil icon next to the job.',
                'Choose one-time or permanent change, pick the new date, and save.',
              ]}
            />
            <FeatureGuide
              title="Today's Jobs, Complete & Skip"
              icon={<CheckCircle />}
              description="Jobs scheduled for today. Complete charges the customer; Skip advances to the next recurring date with no charge."
              steps={[
                'Open Today\'s Jobs on the Dashboard or Scheduling tab.',
                'Complete — when the work is done. Add notes if needed; payment processes and the next visit is scheduled automatically.',
                'Skip — when a visit is not needed (e.g. grass isn\'t growing). No charge for this visit; the next recurring day/date is set and the customer is notified. If a crew skips from the crew app, you also get a dashboard notification and SMS.',
                'Skip is for recurring services only (not one-time / snow). To move a stop to another day instead, use This Week\'s Schedule pencil or the incomplete-stop Reschedule flow.',
              ]}
            />
            <FeatureGuide
              title="Incomplete route stops (must reschedule)"
              icon={<WarningAmber />}
              description="If a crew Ends Route with unfinished stops—or overnight jobs stay past due—you get a dashboard alert (and SMS when possible). Every stop needs an action."
              steps={[
                'A yellow Incomplete route stops banner appears at the top of My Dashboard listing each unfinished stop and crew name.',
                'Click Reschedule on a stop.',
                'Choose Next recurring date to skip this visit (same as Skip — no charge; customer notified of the next date).',
                'Or choose Move to another day — pick the date, then one-time or permanent. This uses the same schedule tools as This Week\'s Schedule; the customer is notified of the new date.',
                'Resolve every listed stop so nothing is left hanging on an old date.',
              ]}
            />
            <FeatureGuide
              title="My Bids (on Dashboard)"
              icon={<Schedule />}
              description="Quick view of bids you submitted. For full bid history and filters, use Reviews & Bids in Command Center."
              steps={[
                'My Bids appears on the Dashboard.',
                'Click the eye icon for details or trash to remove a bid.',
                'Open Reviews & Bids for acceptance rate, filters, and all bid statuses.',
              ]}
            />
            <FeatureGuide
              title="Scheduling tools"
              icon={<SwapHoriz />}
              description="Add to Today, Move Day, Add All, and Edit Schedules live on the Scheduling tab. Moving a date here is the same schedule system used when resolving incomplete route stops."
              steps={[
                'Add to Today — bring accepted services onto today\'s schedule.',
                'Move Day — move all services from one date to another (one-time; recurring day stays the same).',
                'Add All — schedule all unscheduled services of one type to a date (e.g. storm day).',
                'Edit Schedules — open the full editor to change next dates or recurring schedules per customer.',
                'Incomplete stops on the Dashboard — Reschedule → Move uses this same schedule path so one-time vs permanent and customer notices stay consistent.',
              ]}
            />
            <FeatureGuide
              title="Route Optimization"
              icon={<Map />}
              description="Build the day's crew routes: Sort and Optimize assigns stops by service type and optimizes drive order, then Save for the crew app."
              steps={[
                'First set up crew logins and Assign Services in Crew Management → Crews (View & Edit Crew Logins; persists across days).',
                'Open the Route Optimization tab on the Dashboard. Pick the date and number of crews.',
                'Click Sort and Optimize — assigns today\'s stops to crews, then optimizes each route.',
                'Move a stop between crews if needed, then click Re-optimize (keeps your moves; does not re-sort).',
                'Click Save Routes so crews see the day in the crew app. Use View Crew Logins if you need credentials.',
              ]}
            />
            <FeatureGuide
              title="Customer Management"
              icon={<People />}
              description="View customers, services, schedules, and import from CSV."
              steps={[
                'Open the Customer Management tab.',
                'Search or filter, then click a row for details.',
                'Import from CSV to add many customers at once (set prices after import, then send invitations).',
              ]}
            />
            <FeatureGuide
              title="Onboarding existing customers"
              icon={<PersonAdd />}
              description="Invite customers who aren't on the platform yet. They get a signup link only after you send the invite."
              steps={[
                'From Customer Management, invite or onboard a customer.',
                'Enter contact info, address, service type, frequency, and price accurately.',
                'Send the invitation — they create a free account, add payment, and appear in your list.',
                'If you uploaded agreements on Customer Agreements, they e-sign them as part of accepting your offer.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider')}>
                Open My Dashboard
              </Button>
            </Box>
          </Box>
        )}

        {/* Profile */}
        {tab === 'Profile' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your business identity on LawnHub — name, contact info, address, and service areas.
            </Typography>
            <FeatureGuide
              title="Edit profile"
              icon={<Person />}
              description="Update your name, business name, email, phone, address, and service areas."
              steps={[
                'Open Profile from Command Center (or the profile menu).',
                'Edit the fields you want to change.',
                'Save. Customers and bids use this contact information.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/profile')}>
                Open Profile
              </Button>
            </Box>
          </Box>
        )}

        {/* Settings */}
        {tab === 'Settings' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Settings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Account security, notifications, and documents (W-9 required for payouts; insurance and pesticide license for marketplace bidding).
            </Typography>
            <FeatureGuide
              title="Change password & notifications"
              icon={<Settings />}
              description="Update your login password and choose which emails/SMS you receive."
              steps={[
                'Open Settings from Command Center.',
                'Change password in the Password section.',
                'Adjust notification preferences as needed.',
              ]}
            />
            <FeatureGuide
              title="W-9 upload"
              icon={<UploadFile />}
              description="Upload your W-9 for tax purposes. Required for payouts."
              steps={[
                'Go to Settings or the W-9 upload page from the menu/dashboard.',
                'Upload your completed W-9 (PDF or image) and submit.',
              ]}
            />
            <FeatureGuide
              title="Insurance & pesticide license"
              icon={<UploadFile />}
              description="Optional for business tools. Required before you can bid on marketplace requests (pesticide license also required for weed/fert marketplace bids)."
              steps={[
                'Upload liability insurance from Profile (documents section) so admin can verify marketplace bidding.',
                'For Weed Control/Fertilizing marketplace bids, upload your applicator license for verification.',
                'In Settings, manage W-9 and download 1099-NEC tax documents when available.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/settings')}>
                Open Settings
              </Button>
            </Box>
          </Box>
        )}

        {/* My Earnings */}
        {tab === 'My Earnings' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              My Earnings
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Payment and payout status — what you earned, platform fees, and when money moves to your bank. For
              profit/expense tracking, use Financial Dashboard instead.
            </Typography>
            <FeatureGuide
              title="What you'll see"
              icon={<Payments />}
              description="Ready for Payout, Processing, and Payout History — plus marketplace vs onboarded fee breakdown."
              steps={[
                'Open My Earnings from Command Center.',
                'Check Ready for Payout, Processing, and Payout History tabs.',
                'First payouts can have a short hold; weekly transfers use Stripe Connect + W-9 on file.',
                'Marketplace jobs are typically 5% platform fee; onboarded/imported customers and Additional Services are 1%.',
                'For a quick activity list of recent charges, use My Dashboard → Payments.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/earnings')}>
                Open My Earnings
              </Button>
            </Box>
          </Box>
        )}

        {/* Financial Dashboard */}
        {tab === 'Financial Dashboard' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Financial Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              See if the business is making money. Not accounting software — revenue from LawnHub is automatic; you
              enter operating expenses. Use My Earnings for payout status.
            </Typography>
            <FeatureGuide
              title="Dashboard overview"
              icon={<AccountBalance />}
              description="Today / week / month / YTD revenue, expenses, and estimated profit. Forecast and Property Profit tabs look ahead and per property."
              steps={[
                'Open Financial Dashboard from Command Center.',
                'Use the Dashboard tab for summary cards.',
                'For payment and payout status, use My Earnings.',
              ]}
            />
            <FeatureGuide
              title="Entering expenses"
              icon={<AddCircle />}
              description="Enter business costs only (fuel, equipment, chemicals). Platform fees are added automatically."
              steps={[
                'Go to Expenses → Add Expense.',
                'Pick date, category, and amount (vendor and notes optional).',
                'Save. Edit or delete later from the list.',
              ]}
            />
            <FeatureGuide
              title="Forecast: booked revenue ahead"
              icon={<TrendingUp />}
              description="What your active services are scheduled to bring in over the next 30, 60, or 90 days, based on each customer's schedule and price."
              steps={[
                'Open the Forecast tab and pick Next 30, 60, or 90 days.',
                'Per-visit services count every scheduled visit. Pay-in-full seasonal contracts count once, on the first visit, if not paid yet. Monthly contracts count each installment on the 1st.',
                'The notes list anything left out: past-due visits, services that still need a first date, on-demand work like snow removal, and customers on hold.',
                'Pipeline shows money not booked yet: open Additional Services estimates, renewal offers, and invited customers who have not signed up.',
                'The forecast assumes every visit happens; skips and weather are not predicted.',
              ]}
            />
            <FeatureGuide
              title="Property Profit: are you charging enough?"
              icon={<QueryStats />}
              description="Revenue per crew-hour for each property, using time on site from the crew app, plus labor cost and margin when employee pay rates are set."
              steps={[
                'Open the Property Profit tab and pick a period (Last 12 months covers a full season).',
                'Properties earning well below your typical rate per crew-hour are flagged as possibly underpriced. You can set your own target $ per crew-hour instead.',
                'Time only counts when crews tap Arrived and Complete in the crew app. Jobs marked complete from the dashboard are counted as visits but have no time on site.',
                'Add employee pay rates in Crew Management to see labor cost and margin per property.',
                'Check properties and enter a percentage in the Price increase calculator to see what a raise would add over the next 90 days.',
              ]}
            />
            <FeatureGuide
              title="Reports & export"
              icon={<UploadFile />}
              description="Profit & loss, expenses by category, trends, and CSV downloads for your accountant."
              steps={[
                'Open Reports and choose This Month, Quarter, or YTD.',
                'Use Export to download Revenue, Expenses, P&L, and more.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/financial-dashboard')}>
                Open Financial Dashboard
              </Button>
            </Box>
          </Box>
        )}

        {/* Additional Services */}
        {tab === 'Additional Services' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Additional Services
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              One-time Estimates (Fall Cleanup, mulch, aeration, etc.) for customers who already have a core service
              with you. Flat 1% LawnHub fee. Optionally attach a PDF or image (up to 25MB).
            </Typography>
            <FeatureGuide
              title="Create an estimate"
              icon={<AddCircle />}
              description="Only eligible customers appear — those with accepted mowing, weed control, or snow removal."
              steps={[
                'Open Additional Services from Command Center.',
                'Click New Estimate.',
                'Pick the customer, title, description, and price (must be more than $0). Optionally attach a PDF/image quote (max 25MB).',
                'Send. The customer gets an email and text saying an estimate is ready. The alerts do not show a price, because the total they pay adds sales tax and card processing; they see the full breakdown in their account.',
                'The link in the email and text opens the Additional Services estimates in their account (after they sign in). They have 7 days to accept.',
                'If you uploaded an Additional Services agreement on Customer Agreements, the customer signs it when they accept.',
              ]}
            />
            <FeatureGuide
              title="Status badges"
              icon={<CheckCircle />}
              description="Each estimate card shows where it stands."
              steps={[
                'Waiting for customer — sent and not yet accepted.',
                'Accepted — ready to schedule — the estimate is now an Invoice; schedule the job.',
                'Scheduled / In progress / Completed — the job is on the calendar, being worked, or done.',
                'Expired — the customer did not accept within 7 days. Use Edit / Resend to send it again for another 7 days.',
                'Withdrawn — you cancelled the estimate before the customer accepted.',
              ]}
            />
            <FeatureGuide
              title="After accept, edit, resend, or withdraw"
              icon={<Autorenew />}
              description="Accepted estimates become invoices you schedule like any other job. Price or document changes need re-acceptance."
              steps={[
                'After accept, schedule the job from My Dashboard / Scheduling.',
                'In Crew Management → Assign Services, make sure a crew handles Additional Services.',
                'On Route Optimization for that date, use Sort and Optimize (or Assign to Route) so the stop lands on a crew, then Save Routes.',
                'Edit / Resend (waiting, accepted, or expired) to change the price, description, or document. The customer is notified and must accept again; the 7-day window starts over.',
                'You can also View / Replace the document from Reviews & Bids → Bid Details → Estimate Document.',
                'If a card says File missing, the attached document can no longer be opened — use Edit / Resend and Replace file to upload it again.',
                'Withdraw an estimate that is still waiting on the customer to cancel it.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/additional-services')}>
                Open Additional Services
              </Button>
            </Box>
          </Box>
        )}

        {/* Customer Agreements */}
        {tab === 'Customer Agreements' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Customer Agreements
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your own service agreement for each service. Customers review and e-sign it in LawnHub, and you can
              download every signed copy. LawnHub does not review or take part in these agreements — they are between you
              and your customer.
            </Typography>
            <FeatureGuide
              title="Upload your agreements"
              icon={<UploadFile />}
              description="One PDF per service: Lawn Mowing & Trimming, Weed Control & Fertilizing, Snow Removal, and Additional Services (10 MB max)."
              steps={[
                'Open Customer Agreements from Command Center (or the Customer Agreements card in Settings).',
                'Under Your agreements, click Upload PDF next to a service and choose your agreement.',
                'Use View to check it, Replace PDF to upload a new version, or Remove to stop using it.',
                'Replacing a PDF only affects new signatures — signed copies never change. Each upload gets a new version number.',
                'Removing an agreement stops asking new customers to sign it and cancels any unsigned requests. Signed copies are kept.',
              ]}
            />
            <FeatureGuide
              title="Required vs optional"
              icon={<ToggleOn />}
              description="Each uploaded agreement has a switch that controls whether signing is mandatory."
              steps={[
                'Required — customers must sign before they can accept your bid, onboarding offer, or Additional Services estimate.',
                'Optional — customers are asked to sign but can skip it and still accept.',
                'New customers sign automatically as part of accepting; you do not need to send anything.',
              ]}
            />
            <FeatureGuide
              title="Send agreements to existing customers"
              icon={<Send />}
              description="For customers already on LawnHub — send to one or many at once. Only customers with a LawnHub account appear in the list."
              steps={[
                'In Send agreements to existing customers, check which agreements to send.',
                'Leave "Only send each customer the agreements for services they have with you" on so, for example, mowing-only customers do not get the snow agreement. Turn it off to send every checked agreement to everyone selected.',
                'Use the filters (All, Not sent, Pending, Complete) and search to find customers. Check customers one by one or use Select all shown.',
                'Click Send. Customers get an email and sign from their account. Anyone who already signed or already has a pending request is skipped automatically, and a summary shows what was sent.',
              ]}
            />
            <FeatureGuide
              title="Track who signed (Pending / Complete)"
              icon={<Description />}
              description="Each customer shows a status for every agreement that applies to them."
              steps={[
                'Pending (sent date) — the agreement was sent and is waiting for the customer to sign.',
                'Complete (signed date) — the customer signed and returned it.',
                'Not sent — nothing has been sent for that agreement yet.',
                'Signed older version — they signed a PDF you have since replaced. Send again if you want the new version signed.',
                'Use the Not sent filter, then Select all shown, to quickly send to everyone who still needs an agreement.',
              ]}
            />
            <FeatureGuide
              title="Download signed copies"
              icon={<Download />}
              description="The Sent agreements list at the bottom has Complete and Pending tabs."
              steps={[
                'Open the Complete tab and search by name, address, or zip.',
                'Click Download PDF. Files are named Last name_Zip_Service_Date so they are easy to file.',
                'On the Pending tab, use Cancel request to withdraw an agreement you sent that the customer has not signed yet.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/customer-agreements')}>
                Open Customer Agreements
              </Button>
            </Box>
          </Box>
        )}

        {/* Crew Management */}
        {tab === 'Crew Management' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Crew Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set up crew logins and which service types each crew handles, then optionally track employees, time
              clock, payroll CSV, and productivity. Route Optimization uses your saved assignments each day.
            </Typography>
            <FeatureGuide
              title="Crew logins & Assign Services"
              icon={<Groups />}
              description="Do this once on the Crews tab — it persists. Crew name and password live only in View & Edit Crew Logins."
              steps={[
                'Open Crew Management → Crews.',
                'Choose number of crews and click View & Edit Crew Logins (creates any missing logins, then opens credentials).',
                'Use Edit to change a crew name or password. Username is what they type to sign in.',
                'Click Assign Services and check Lawn Mowing, Weed/Fert, Snow, and/or Additional Services per crew.',
                'Click each crew card to set employee roster and optional Lead (Save). Names are not edited here.',
              ]}
            />
            <FeatureGuide
              title="Employees, time clock & payroll"
              icon={<Groups />}
              description="Add employees with pay rates and export payroll-ready CSV. Pay rates never show in the crew app."
              steps={[
                'Open Employees and add people with hourly or daily rates (max 4 per crew roster).',
                'Assign them on the Crews tab (roster / Lead). Leaders Start/End Route and lunch in the crew app.',
                'Use the Time Clock tab to review Start/End Route times for the week and Close Shift if a crew forgot End Route.',
                'Export payroll CSV for a date range from Payroll Export.',
              ]}
            />
            <FeatureGuide
              title="Productivity"
              icon={<Dashboard />}
              description="Revenue per labor hour, efficiency vs your historical pace, and labor cost % by employee or crew."
              steps={[
                'Go to Productivity, set dates, choose Employee or Crew view, then Update.',
                'Hover column info icons for definitions.',
                'Click a row for weekly trend charts.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/crew-management')}>
                Open Crew Management
              </Button>
            </Box>
          </Box>
        )}

        {/* Crew App */}
        {tab === 'Crew App' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Crew App
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Mobile app crews use in the field. Set up logins and Assign Services in Crew Management first, then
              Save Routes from Route Optimization so today&apos;s stops appear.
            </Typography>
            <FeatureGuide
              title="Sign in & Start Route"
              icon={<PlayArrow />}
              description="Each crew has a username and password from View & Edit Crew Logins (not the provider email login)."
              steps={[
                'Open the crew app URL and sign in with that crew\'s Username and password.',
                'You\'ll see Today\'s Route and a Time Clock card (stays pinned at the top while you scroll stops).',
                'Optional but recommended: tap Start Route when the crew begins work — this powers time clock and productivity metrics.',
              ]}
            />
            <FeatureGuide
              title="Working a stop"
              icon={<CameraAlt />}
              description="Work stops in order. Use On the way, Navigate, Arrived, Photos, then Job Complete—or Skip Visit if the stop is not needed."
              steps={[
                'On the way — texts the customer that you\'re heading over (once per stop).',
                'Navigate — opens Google Maps turn-by-turn to the address.',
                'Arrived — check in when you get on site (starts job timer).',
                'Photos — take before and after photos for the job record.',
                'Text Customer — optional message after you\'ve arrived.',
                'Job Complete — finish the stop; confirm which crew members worked it if toggles appear.',
                'Skip Visit — if the service isn\'t needed this cycle (e.g. grass isn\'t growing). No charge; next recurring date is set and the customer is notified. Your provider also gets a dashboard alert and SMS. Recurring services only.',
              ]}
            />
            <FeatureGuide
              title="Lunch break (one per route)"
              icon={<Restaurant />}
              description="Lunch must be started and ended so efficiency ratings exclude break time. Job actions lock until lunch ends."
              steps={[
                'Tap Start Lunch when the crew goes on break. The Time Clock highlights and End Lunch becomes a large red button.',
                'Job buttons (Arrived, Complete, Skip, Photos, etc.) stay locked until you End Lunch — so lunch time is recorded accurately.',
                'Tap End Lunch when work resumes. Start Lunch then shows Lunch done (one lunch per route).',
                'After lunch, End Route is larger so you remember to clock out at the end of the day.',
              ]}
            />
            <FeatureGuide
              title="End Route"
              icon={<Stop />}
              description="Close the shift when the crew is done. Unfinished stops are sent to the provider to reschedule—crew is not blocked from ending the route."
              steps={[
                'Mark finished stops Job Complete (or Skip Visit if not needed) before ending when you can.',
                'Tap End Route on the Time Clock and confirm — always allowed even if some stops remain.',
                'If stops are still incomplete, your provider gets a dashboard alert and SMS (when available) with the crew name and how many stops need rescheduling.',
                'The provider must choose next recurring date or move each leftover stop to another day — nothing should be left stuck on a past date.',
                'If you forgot lunch, you can still End Route — but Start/End Lunch during the day gives cleaner productivity data.',
              ]}
            />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Tip: Jobs completed count and Time Clock stay pinned at the top while you scroll stops.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/crew-management')}>
                Open Crew Management (logins)
              </Button>
            </Box>
          </Box>
        )}

        {/* Reviews & Bids */}
        {tab === 'Reviews & Bids' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Reviews & Bids
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              See customer ratings and your full bid history in one place — acceptance rate, open bids, and review
              comments.
            </Typography>
            <FeatureGuide
              title="Customer reviews"
              icon={<Star />}
              description="Star ratings and written feedback from customers after completed jobs."
              steps={[
                'Open Reviews & Bids from Command Center.',
                'At the top, check your average rating and total review count.',
                'Scroll the reviews list for comments, service type, and date.',
                'Use this feedback to improve service — ratings show on your public profile where applicable.',
              ]}
            />
            <FeatureGuide
              title="Bid performance & All Bids"
              icon={<RateReview />}
              description="Acceptance rate and a filterable list of every bid you've submitted."
              steps={[
                'Review Bid Acceptance Rate and counts (total, accepted, open, rejected).',
                'In All Bids, use the filter tabs: All, Open, Accepted, Rejected, or Deleted.',
                'Click the eye icon on a bid to see price, services, address, and request details.',
                'For Additional Services bids, Bid Details shows Estimate Document — View, Attach, Replace, or Remove.',
                'Changing the estimate document after accept requires the customer to accept again.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/performance')}>
                Open Reviews & Bids
              </Button>
            </Box>
          </Box>
        )}

        {/* Service History / Cancellations */}
        {tab === 'Service History / Cancellations' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Service History / Cancellations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Look up completed jobs and see services customers cancelled or that are pending cancellation.
            </Typography>
            <FeatureGuide
              title="Service History tab"
              icon={<History />}
              description="Your recent completed services. Search by customer, address, zip, phone, service type, or date range."
              steps={[
                'Open Service History / Cancellations from Command Center.',
                'Stay on the Service History tab (default).',
                'By default you see your most recent services — use search or date filters for older jobs.',
                'Click a row (or the view icon) to open that job\'s details — including before/after photos when available.',
              ]}
            />
            <FeatureGuide
              title="Cancellations tab"
              icon={<Cancel />}
              description="Cancelled and pending-cancellation services, with reason and date when available."
              steps={[
                'Switch to the Cancellations tab.',
                'Review customer, service, address, status (Cancelled vs Pending Cancellation), date, and reason.',
                'Pending Cancellation means the request is awaiting admin confirmation.',
                'Click a row to open job details for more context.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/service-history')}>
                Open Service History / Cancellations
              </Button>
            </Box>
          </Box>
        )}

        {/* Renewals */}
        {tab === 'Renewals' && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Renewals
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Send seasonal renewal offers and track whether customers accept or decline. Same-provider renewals use a
              1% platform fee.
            </Typography>
            <FeatureGuide
              title="View and send renewals"
              icon={<Autorenew />}
              description="Eligible seasonal customers appear after you Mark Season Ended in Customer Management."
              steps={[
                'In Customer Management, Mark Season Ended for customers finishing the season.',
                'Open Renewals from Command Center.',
                'Set or edit the renewal price, then Send or Resend (email/SMS). Offers do not auto-expire.',
                'For monthly contracts, wait until payments for the season are complete before sending.',
                'Status updates when the customer accepts, declines, or is matched elsewhere. Withdraw a pending offer if needed.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/provider/renewals')}>
                Open Renewals
              </Button>
            </Box>
          </Box>
        )}

        {/* AI Assistant (Pilot) */}
        {tab === 'AI Assistant (Pilot)' && (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6">AI Assistant</Typography>
              <Chip label="Pilot" size="small" color="warning" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Connect the AI assistant you already use to your LawnHub account. Ask it about your schedule, routes,
              customers, and numbers in plain words — and, only if you allow it, have it move or skip visits, put a whole
              service on one day, or add a new customer. Every change is shown to you first and only happens after you say yes.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              LawnHub doesn’t charge for this and doesn’t run an AI model — your assistant’s own plan and pricing apply.
              This is a pilot: it works on your real account, and we’re still improving it based on what providers tell us.
            </Typography>
            <FeatureGuide
              title="What your assistant can see and do"
              icon={<SmartToyOutlined />}
              stepsLabel="Included:"
              description="You choose permissions for each assistant. View permissions only read your data. Change permissions are off until you turn them on."
              steps={[
                'View schedule & routes — what is scheduled on any day (customer, address, service, status) and how many stops each crew has done and has left.',
                'View customers — find a customer by name, email, street, or city; list and count your customers by service (mowing, weed control, snow removal); see who has not signed their agreement; see renewal offers still waiting on customers.',
                'View financials — booked revenue for the next 30, 60, or 90 days (plus open estimates and renewals), your estimated next payout, and revenue per crew-hour by property.',
                'Change schedule (off unless you turn it on) — move every unfinished visit from one day to another (rain or a storm), move specific visits such as one crew’s remaining stops, put every customer of one service on a single day (for example, all snow removal today), or skip a customer’s next recurring visit with no charge.',
                'Add customers (off unless you turn it on) — add a new lawn mowing customer and send them the LawnHub invitation by email, plus text if you give a phone number. They accept and set up payment themselves; nothing is charged by adding them. If you already have a customer at that address, the agent won’t add them again or send another invitation.',
                'What it can never do — charge, refund, or pay out money; change prices; cancel services; set up seasonal contracts or other services; or see any other provider’s data.',
              ]}
            />
            <FeatureGuide
              title="Before you start"
              icon={<Checklist />}
              stepsLabel="Checklist:"
              description="You need an assistant that can connect to outside apps, and you need to be signed in as the business owner."
              steps={[
                'An AI assistant that can call outside apps using an OpenAPI document (sometimes called an API schema or spec) and an API key. ChatGPT custom GPTs with Actions work this way (creating a custom GPT needs a paid ChatGPT plan). Other agents that can use an API key and an OpenAPI document work too.',
                'Claude’s apps and connectors use a different connection type (MCP) that LawnHub doesn’t offer yet. Claude tools that can call a web API with an API key can use the same steps as “Other assistants” below.',
                'Sign in to LawnHub with your provider (business owner) login — not a crew login.',
                'If you don’t see Connected AI Agents in Settings, the pilot isn’t turned on for your account yet. Contact LawnHub and we’ll check.',
                'Read the Connected AI Agents terms (Provider Service Agreement §14.1.4, on the Legal page). Your AI assistant is your choice and your responsibility: you are fully responsible and liable for everything it does with your account, and LawnHub is not responsible for its actions.',
                'LawnHub’s help is limited to these setup guides and LawnHub itself. For problems with your assistant — its account, settings, answers, or behavior — contact the company that makes it.',
              ]}
            />
            <FeatureGuide
              title="Step 1: Create a key in LawnHub"
              icon={<VpnKey />}
              description="Each assistant gets its own key, so you can see what each one did and shut one off without affecting the others."
              steps={[
                'Open Settings from Command Center and scroll to Connected AI Agents (marked Pilot).',
                'Click Connect an agent.',
                'Under Which AI assistant?, pick ChatGPT, Muse, or Claude — or Other and type its name. This helps us see which assistants work best in the pilot.',
                'The Name fills in with the assistant. Change it if you connect more than one, for example “ChatGPT – office” and “ChatGPT – phone,” so you can tell them apart in Activity.',
                'Under What this agent can see, all three view permissions are ticked to start. Untick anything you don’t want it to read.',
                'Under What this agent can change, leave both boxes off for now. You can turn on Change schedule or Add customers later with Edit — no new key needed.',
                'Tick the box agreeing to the Connected AI Agents terms. You can’t create a key without it.',
                'Click Create key. LawnHub shows three things, each with a copy button: Agent key, API address, and OpenAPI document.',
                'Copy the key into a safe place such as a password manager. LawnHub only shows it once — if you lose it, use New key.',
                'Treat the key like a password. Anyone who has it can do whatever you allowed that agent to do.',
                'Click I’ve copied it. Your agent now appears in the list as “Not used yet.”',
              ]}
            />
            <FeatureGuide
              title="Step 2: Connect ChatGPT (custom GPT)"
              icon={<LinkIcon />}
              description="ChatGPT connects to LawnHub through a private custom GPT with an Action. Menu names can shift a little as ChatGPT updates."
              steps={[
                'In ChatGPT, open GPTs (Explore GPTs) and click Create.',
                'Open the Configure tab. Give it a name such as “LawnHub Assistant.”',
                'In Instructions, paste: “Use the LawnHub actions to answer questions about my lawn care business. Before any change, show me the preview summary and wait for me to say yes before confirming. If I say no, cancel it.”',
                'Scroll down to Actions and click Create new action.',
                'Click Import from URL and paste the OpenAPI document address you copied from LawnHub. The LawnHub actions (get_schedule, find_customer, and so on) appear in the list.',
                'Under Authentication, choose API Key, set Auth Type to Bearer, paste your agent key, and save.',
                'When you save the GPT, choose Only me. If you share the GPT, anyone using it acts on your account with your key.',
                'Start a chat with your GPT and ask “What’s on my schedule today?” ChatGPT may ask whether to allow the GPT to talk to the LawnHub site — choose Allow (or Always allow). That prompt comes from ChatGPT, not LawnHub.',
              ]}
            />
            <FeatureGuide
              title="Step 2: Other assistants"
              icon={<LinkIcon />}
              description="Most assistants that can use outside apps need the same two things: where LawnHub is, and the key."
              steps={[
                'In your assistant’s settings, look for Actions, Tools, Custom APIs, Integrations, or Plugins.',
                'If it can import an OpenAPI document (also called a schema or spec URL), paste the OpenAPI document address from LawnHub.',
                'Set authentication to API key or Bearer token and paste your agent key. If it asks for a header, the header is Authorization and the value is the word Bearer, a space, then your key.',
                'If your assistant only takes setup instructions in the chat, give it the API address and OpenAPI document address, and the key for the Authorization header. Do this only in a private chat you never share. If that chat is ever shared or exposed, use New key in LawnHub right away.',
                'Some assistants ask your permission each time before sending information to LawnHub. That is normal — it’s your assistant checking with you. Many have an “always allow” option.',
              ]}
            />
            <FeatureGuide
              title="Step 3: Try it — good first questions"
              icon={<ChatOutlined />}
              description="Start with questions you already know the answer to, and check the first few answers against your dashboard."
              steps={[
                '“What’s on my schedule today?” or “What do I have on Friday?”',
                '“How many stops are left on Crew 2’s route?”',
                '“How many mowing customers do I have?” or “Find Jane Smith.”',
                '“Who hasn’t signed their agreement yet?”',
                '“What revenue is booked for the next 60 days?” and “When is my next payout?”',
                '“Which properties earn the least per crew-hour?”',
                'In Settings → Connected AI Agents, click Activity on the agent to see each request it made.',
              ]}
            />
            <FeatureGuide
              title="Making changes: preview first, then you say yes"
              icon={<FactCheck />}
              description="Changes always take two steps. Your assistant asks LawnHub for a preview, shows it to you, and only confirms after you say yes."
              steps={[
                'Turn on changes: Settings → Connected AI Agents → Edit on the agent → tick Change schedule and/or Add customers → Save. It takes effect right away with the same key.',
                'Ask in plain words, for example: “Move the rest of today’s stops to Friday.” “Add all snow removal customers to today.” “Skip Bob Smith’s next mowing visit.” “Add a new weekly mowing customer: Jane Doe, 123 Main St, Homewood IL 60430, jane@example.com, $45 a visit.”',
                'Your assistant shows a preview: what will change, how many visits, and how many customers will be notified. At this point nothing has changed.',
                'Read the preview. Say yes to go ahead, or no / cancel to stop. Your assistant should never confirm without your yes — if it does, revoke its key and send us feedback.',
                'A preview lasts 10 minutes and can be used once. If your schedule changed after the preview (for example, a crew finished a stop), LawnHub refuses it and nothing changes — ask for a fresh preview.',
                'Moves are one-time: each customer’s regular service day stays the same. Customers get the same texts and emails as when you make the change in the dashboard.',
                'Each agent has a daily limit on confirmed changes (currently 25 in 24 hours).',
              ]}
            />
            <FeatureGuide
              title="Activity, new keys, and shutting it off"
              icon={<ManageHistory />}
              description="Everything an agent does is logged, and you can cut off access instantly."
              steps={[
                'Activity — every lookup, preview, confirmation, cancellation, and refusal, with the time and result. “Not allowed” means the agent tried something you haven’t permitted; nothing changed.',
                'Edit — change the name or permissions at any time. Takes effect immediately.',
                'New key — use this if the key may have been seen by someone else or pasted somewhere public. The old key stops working immediately; paste the new key into your assistant.',
                'Revoke — the agent loses access immediately. You can connect it again later with a new key.',
                'You can have up to 5 agents connected at once.',
              ]}
            />
            <FeatureGuide
              title="Troubleshooting"
              icon={<HelpOutline />}
              stepsLabel="Common messages:"
              description="What your assistant might tell you, and what to do."
              steps={[
                '“This connection doesn’t have the … permission” — click Edit on the agent in Settings and tick that permission.',
                '“Changes through agents are switched off in LawnHub right now” — LawnHub has paused changes for everyone. Viewing still works; try again later.',
                '“This agent key has been revoked or does not exist” or “Missing or invalid agent key” — the key was revoked, replaced, or pasted incompletely. Use New key and paste the whole new key into your assistant.',
                '“Agent access is not available for this LawnHub account” — the pilot isn’t turned on for your account. Contact LawnHub.',
                '“This preview expired” or “Your schedule changed since this preview” — nothing changed. Ask for a fresh preview.',
                '“Too many agent requests” — your assistant made a lot of calls in a short time. Wait a few minutes (at most about 15) and try again.',
                '“Already your customer at …” — you already have a customer at that address. Update them in the dashboard instead.',
                'An answer looks wrong — check it in the dashboard, then click Send feedback in Settings → Connected AI Agents with what you asked and what the assistant said.',
              ]}
            />
            <FeatureGuide
              title="Pilot feedback"
              icon={<FeedbackIcon />}
              description="Real-world feedback decides what we improve next."
              steps={[
                'In Settings → Connected AI Agents, click Send feedback.',
                'Choose Something worked well, Something went wrong, or Idea or request, pick the agent if it’s about one, and describe it.',
                'Your message goes straight to the LawnHub team.',
              ]}
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={() => navigate('/settings')}>
                Open Settings
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={() => navigate('/provider')}>
          Back to Dashboard
        </Button>
      </Box>
    </Container>
  );
};

export default ProviderTutorialsPage;
